import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, X, Send, Ship, ExternalLink, CheckCircle2, Trash2, Sparkles, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtEur, fmtDate } from "@/admin/adminLabels";

const PUBLIC_SITE = process.env.REACT_APP_BACKEND_URL?.replace("https://", "https://")?.replace(/\/$/, "") || "";

function emptyForm() {
  return {
    charter_company: "",
    boat_model_name: "",
    boat_category: "",
    boat_length_ft: "",
    boat_cabins: "",
    boat_berths: "",
    boat_description: "",
    image_url: "",
    price_eur: "",
    departure_port: "",
    destination: "",
    available_dates: "",
    includes: "",
    excludes: "",
    skipper_included: "",
    internal_notes: "",
    proposal_type: "principale",
  };
}

const PROPOSAL_TYPES = [
  { value: "principale",           label: "Proposta principale (come richiesto)" },
  { value: "cambio_imbarcazione",  label: "Cambio imbarcazione" },
  { value: "cambio_date",          label: "Cambio date" },
  { value: "cambio_destinazione",  label: "Cambio destinazione" },
  { value: "alternativa_economica",label: "Alternativa economica" },
  { value: "alternativa_premium",  label: "Alternativa premium" },
];

export default function ProposalsSection({ lead, onLeadUpdated }) {
  const [fleet, setFleet] = useState([]);
  const [categoryImages, setCategoryImages] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/fleet").then(r => {
      setFleet(r.data.boats || []);
      setCategoryImages(r.data.category_images || {});
    }).catch(() => {});
  }, []);

  const proposals = lead?.proposals || [];

  const onSelectBoat = (modelName) => {
    const boat = fleet.find(b => b.model_name === modelName);
    if (!boat) return;
    setForm(f => ({
      ...f,
      boat_model_name: boat.model_name,
      boat_category: boat.category,
      boat_length_ft: boat.length_ft,
      boat_cabins: boat.cabins,
      boat_berths: boat.berths,
      boat_description: boat.description,
      image_url: boat.image_url,
    }));
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setShowForm(true);
    setForm({
      charter_company: p.charter_company || "",
      boat_model_name: p.boat_model_name || "",
      boat_category: p.boat_category || "",
      boat_length_ft: p.boat_length_ft ?? "",
      boat_cabins: p.boat_cabins ?? "",
      boat_berths: p.boat_berths ?? "",
      boat_description: p.boat_description || "",
      image_url: p.image_url || "",
      price_eur: p.price_eur ?? "",
      departure_port: p.departure_port || "",
      destination: p.destination || "",
      available_dates: p.available_dates || "",
      includes: p.includes || "",
      excludes: p.excludes || "",
      skipper_included: p.skipper_included == null ? "" : (p.skipper_included ? "yes" : "no"),
      internal_notes: p.internal_notes || "",
    });
  };

  const startNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const save = async () => {
    if (!form.charter_company || !form.boat_model_name || !form.price_eur) {
      toast.error("Compila almeno: charter, modello e prezzo");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        boat_length_ft: form.boat_length_ft ? parseInt(form.boat_length_ft) : null,
        boat_cabins: form.boat_cabins !== "" ? parseInt(form.boat_cabins) : null,
        boat_berths: form.boat_berths !== "" ? parseInt(form.boat_berths) : null,
        price_eur: parseFloat(form.price_eur),
        skipper_included: form.skipper_included === "yes" ? true : form.skipper_included === "no" ? false : null,
      };
      // Auto-fill image from category if not selected from fleet
      if (!body.image_url && body.boat_category && categoryImages[body.boat_category]) {
        body.image_url = categoryImages[body.boat_category];
      }
      let res;
      if (editingId) {
        res = await api.put(`/admin/leads/${lead.id}/proposals/${editingId}`, body);
        onLeadUpdated?.(res.data);
      } else {
        await api.post(`/admin/leads/${lead.id}/proposals`, body);
        const r = await api.get(`/admin/leads/${lead.id}`);
        onLeadUpdated?.(r.data);
      }
      toast.success(editingId ? "Preventivo aggiornato" : "Preventivo aggiunto");
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally { setSaving(false); }
  };

  const onDelete = async (pid) => {
    if (!confirm("Eliminare questo preventivo?")) return;
    try {
      const r = await api.delete(`/admin/leads/${lead.id}/proposals/${pid}`);
      onLeadUpdated?.(r.data);
      toast.success("Preventivo eliminato");
    } catch (e) { toast.error("Errore"); }
  };

  const onSendToClient = async () => {
    if (proposals.length === 0) {
      toast.error("Aggiungi almeno un preventivo prima di inviare");
      return;
    }
    if (!confirm(`Inviare ${proposals.length} preventivo/i al cliente ${lead.email}?`)) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/leads/${lead.id}/send-proposals`);
      onLeadUpdated?.(r.data.lead);
      toast.success("Email inviata al cliente");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally { setSending(false); }
  };

  const portalUrl = lead?.public_token
    ? `${window.location.origin}/proposta/${lead.public_token}`
    : null;

  // Group fleet by category for select
  const fleetByCategory = useMemo(() => {
    const map = {};
    fleet.forEach(b => {
      if (!map[b.category]) map[b.category] = [];
      map[b.category].push(b);
    });
    return map;
  }, [fleet]);

  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-5 space-y-4" data-testid="proposals-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-ink font-semibold">Preventivi ({proposals.length})</p>
          {lead?.proposals_sent_at && (
            <p className="text-[11px] text-green-700 mt-0.5 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Inviati al cliente il {fmtDate(lead.proposals_sent_at)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {portalUrl && (
            <Button variant="outline" size="sm" data-testid="copy-portal-link"
                    onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Link copiato"); }}
                    className="text-xs border-navy/20 text-navy hover:bg-sand h-8">
              <Copy className="h-3 w-3 mr-1" /> Link portale
            </Button>
          )}
          <Button data-testid="add-proposal-btn" onClick={startNew} className="bg-navy text-white hover:bg-[#06101f] h-9">
            <Plus className="h-4 w-4 mr-1" /> Aggiungi preventivo
          </Button>
        </div>
      </div>

      {/* Alert cliente interessato */}
      {lead?.status === "cliente_interessato" && (
        <div data-testid="client-interest-alert" className="rounded-lg border border-gold bg-gold/10 p-3 flex items-start gap-2">
          <Sparkles className="h-4 w-4 text-gold mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-navy">Il cliente ha selezionato una proposta!</p>
            <p className="text-xs text-ink mt-1">Contatta la società charter per la conferma finale di disponibilità.</p>
          </div>
        </div>
      )}

      {/* Proposals list */}
      {proposals.length === 0 && !showForm && (
        <p className="text-sm text-ink/60 text-center py-8">Nessun preventivo ancora inserito.</p>
      )}

      {proposals.map((p) => {
        const fallbackImg = categoryImages[p.boat_category] || categoryImages["Barca a vela"];
        const imgSrc = p.image_url || fallbackImg;
        return (
        <div key={p.id} data-testid={`proposal-card-${p.id}`}
             className={`rounded-lg border p-3 flex gap-3 ${p.client_interested ? 'border-gold bg-gold/5' : 'border-[#E2E8F0] bg-sand/40'}`}>
          <img
            src={imgSrc}
            alt={p.boat_model_name}
            className="h-20 w-28 rounded-md object-cover shrink-0 bg-sand"
            onError={(e) => { e.currentTarget.src = fallbackImg; }}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                {p.proposal_type && p.proposal_type !== "principale" && (
                  <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-gold/20 text-navy">
                    {(PROPOSAL_TYPES.find(t => t.value === p.proposal_type) || {}).label || p.proposal_type}
                  </span>
                )}
                <p className={`${p.proposal_type && p.proposal_type !== "principale" ? "mt-1 " : ""}font-semibold text-navy truncate`}>{p.boat_model_name}</p>
                <p className="text-xs text-ink/70 truncate">{p.charter_company} · {p.boat_category}</p>
              </div>
              <p className="font-serif text-lg text-navy whitespace-nowrap">{fmtEur(p.price_eur)}</p>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-ink">
              {p.boat_length_ft && <span>{p.boat_length_ft} ft</span>}
              {p.boat_cabins != null && <span>{p.boat_cabins} cab</span>}
              {p.boat_berths != null && <span>{p.boat_berths} posti</span>}
              {p.destination && <span>· {p.destination}</span>}
              {p.departure_port && <span>· da {p.departure_port}</span>}
              {p.available_dates && <span>· {p.available_dates}</span>}
            </div>
            {p.client_interested && (
              <p className="mt-1.5 text-xs text-gold font-semibold inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Selezionato dal cliente
              </p>
            )}
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => startEdit(p)}
                      className="h-7 text-xs border-navy/20 text-navy hover:bg-white">Modifica</Button>
              <Button size="sm" variant="outline" onClick={() => onDelete(p.id)}
                      className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50">
                <Trash2 className="h-3 w-3 mr-1" /> Elimina
              </Button>
            </div>
          </div>
        </div>
        );
      })}

      {/* Form */}
      {showForm && (
        <div className="rounded-lg border-2 border-navy/30 bg-sand/30 p-4 space-y-3" data-testid="proposal-form">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-navy">{editingId ? "Modifica preventivo" : "Nuovo preventivo"}</p>
            <button onClick={() => { setShowForm(false); setEditingId(null); }}>
              <X className="h-4 w-4 text-ink" />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-ink">Tipologia proposta</Label>
              <Select value={form.proposal_type || "principale"} onValueChange={(v) => setForm({ ...form, proposal_type: v })}>
                <SelectTrigger data-testid="proposal-type-select" className="mt-1 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROPOSAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-ink/50 mt-1">Aiuta il cliente a capire perché stai proponendo questa opzione.</p>
            </div>

            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-ink">Imbarcazione (dal Fleet Database)</Label>
              <Select value={form.boat_model_name} onValueChange={onSelectBoat}>
                <SelectTrigger data-testid="boat-select" className="mt-1 h-10">
                  <SelectValue placeholder="Seleziona modello dal database…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {Object.entries(fleetByCategory).map(([cat, boats]) => (
                    <React.Fragment key={cat}>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-ink/50 bg-sand">{cat}</div>
                      {boats.map(b => (
                        <SelectItem key={b.model_name} value={b.model_name}>
                          {b.model_name} ({b.length_ft}ft · {b.berths}p)
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-ink/50 mt-1">Seleziona dal database: categoria, cabine, posti, descrizione e immagine si compilano automaticamente.</p>
            </div>

            <div><Label className="text-xs">Società charter *</Label>
              <Input value={form.charter_company} onChange={(e) => setForm({ ...form, charter_company: e.target.value })}
                     placeholder="es. Top Charter Sardegna" className="mt-1 h-10" />
            </div>
            <div><Label className="text-xs">Prezzo € *</Label>
              <Input type="number" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })}
                     placeholder="5000" className="mt-1 h-10" />
            </div>

            <div><Label className="text-xs">Modello (override manuale)</Label>
              <Input value={form.boat_model_name} onChange={(e) => setForm({ ...form, boat_model_name: e.target.value })}
                     className="mt-1 h-10" />
            </div>
            <div><Label className="text-xs">Categoria</Label>
              <Select value={form.boat_category} onValueChange={(v) => {
                setForm(f => ({ ...f, boat_category: v, image_url: f.image_url || categoryImages[v] || "" }));
              }}>
                <SelectTrigger className="mt-1 h-10"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {Object.keys(categoryImages).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div><Label className="text-xs">Lunghezza (ft)</Label>
              <Input type="number" value={form.boat_length_ft} onChange={(e) => setForm({ ...form, boat_length_ft: e.target.value })} className="mt-1 h-10" />
            </div>
            <div><Label className="text-xs">Cabine</Label>
              <Input type="number" value={form.boat_cabins} onChange={(e) => setForm({ ...form, boat_cabins: e.target.value })} className="mt-1 h-10" />
            </div>
            <div><Label className="text-xs">Posti letto</Label>
              <Input type="number" value={form.boat_berths} onChange={(e) => setForm({ ...form, boat_berths: e.target.value })} className="mt-1 h-10" />
            </div>
            <div><Label className="text-xs">Skipper incluso?</Label>
              <Select value={form.skipper_included} onValueChange={(v) => setForm({ ...form, skipper_included: v })}>
                <SelectTrigger className="mt-1 h-10"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Sì</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div><Label className="text-xs">Destinazione / area</Label>
              <Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })}
                     placeholder="es. Costa Smeralda" className="mt-1 h-10" />
            </div>
            <div><Label className="text-xs">Porto di partenza</Label>
              <Input value={form.departure_port} onChange={(e) => setForm({ ...form, departure_port: e.target.value })}
                     placeholder="es. Olbia" className="mt-1 h-10" />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-xs">Date disponibili</Label>
              <Input value={form.available_dates} onChange={(e) => setForm({ ...form, available_dates: e.target.value })}
                     placeholder="es. 5-12 luglio 2026 oppure 28 giu – 5 lug" className="mt-1 h-10" />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-xs">Cosa include</Label>
              <Textarea rows={2} value={form.includes} onChange={(e) => setForm({ ...form, includes: e.target.value })}
                        placeholder="es. Assicurazione casco, biancheria, lenzuola, dotazioni sicurezza" className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Cosa NON include</Label>
              <Textarea rows={2} value={form.excludes} onChange={(e) => setForm({ ...form, excludes: e.target.value })}
                        placeholder="es. Cambusa, carburante, ormeggi, pulizie finali" className="mt-1" />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-xs">Note interne (solo admin)</Label>
              <Textarea rows={2} value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                        placeholder="Promemoria su charter, condizioni, ecc." className="mt-1" />
            </div>
          </div>

          <div className="flex gap-2">
            <Button data-testid="save-proposal" onClick={save} disabled={saving} className="bg-navy text-white hover:bg-[#06101f] flex-1 h-11">
              {saving ? "Salvataggio…" : (editingId ? "Aggiorna" : "Aggiungi preventivo")}
            </Button>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }} className="border-navy/20 text-navy hover:bg-sand h-11">
              Annulla
            </Button>
          </div>
        </div>
      )}

      {/* Send button */}
      {proposals.length > 0 && (
        <div className="pt-3 border-t border-[#F1F5F9]">
          <Button data-testid="send-proposals-btn" onClick={onSendToClient} disabled={sending}
                  className="w-full bg-gold text-navy hover:bg-[#b48e57] h-11 font-semibold">
            <Send className="h-4 w-4 mr-2" />
            {sending ? "Invio in corso…" : (lead?.proposals_sent_at ? "Re-invia preventivi al cliente" : "Invia preventivi al cliente")}
          </Button>
          {portalUrl && (
            <a href={portalUrl} target="_blank" rel="noreferrer" data-testid="open-portal"
               className="mt-2 inline-flex items-center text-xs text-navy hover:underline">
              <ExternalLink className="h-3 w-3 mr-1" /> Apri portale cliente in nuova scheda
            </a>
          )}
        </div>
      )}
    </div>
  );
}
